import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Alert,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Folder,
  Settings,
  Refresh,
  InsertDriveFile,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store/store';
import {
  fetchCustomerUploads,
  fetchCustomerCases,
} from '../store/slices/customerAuthSlice';
import { sanitizeText, sanitizeError, sanitizeFileSize } from '../utils/sanitize';

// Helper to format date
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Status chip component for uploads
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
      case 'corrupted':
        return 'error';
      case 'uploading':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle fontSize="small" />;
      case 'failed':
      case 'corrupted':
        return <ErrorIcon fontSize="small" />;
      case 'uploading':
        return <UploadIcon fontSize="small" />;
      case 'pending':
        return <HourglassEmpty fontSize="small" />;
      default:
        return null;
    }
  };

  return (
    <Chip
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      color={getStatusColor()}
      size="small"
      icon={getStatusIcon() || undefined}
    />
  );
};

// Priority chip component for cases
const PriorityChip: React.FC<{ priority: string }> = ({ priority }) => {
  const getPriorityColor = (): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    switch (priority?.toLowerCase()) {
      case 'critical':
      case 'emergency':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={priority || 'N/A'}
      color={getPriorityColor()}
      size="small"
      variant="outlined"
    />
  );
};

const CustomerDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Authentication is now handled by ProtectedRoute wrapper
  // This component only renders when user is authenticated
  const { user, dashboard } = useSelector(
    (state: RootState) => state.customerAuth
  );

  const { uploads, cases } = dashboard;

  // Fetch dashboard data on mount with abort on unmount (Task #4212)
  // ProtectedRoute guarantees user is authenticated before rendering
  useEffect(() => {
    // Dispatch thunks and store the promises for cleanup
    const uploadsPromise = dispatch(fetchCustomerUploads({ page: 1, page_size: 5 }));
    const casesPromise = dispatch(fetchCustomerCases({ page: 1, page_size: 5 }));

    // Cleanup: abort in-flight requests on unmount to prevent memory leaks
    return () => {
      uploadsPromise.abort();
      casesPromise.abort();
    };
  }, [dispatch]);

  // Handle refresh with double-click protection (Task #4215)
  const handleRefresh = () => {
    // Prevent duplicate refresh requests while loading
    if (uploads.loading || cases.loading) {
      return;
    }
    dispatch(fetchCustomerUploads({ page: 1, page_size: 5 }));
    dispatch(fetchCustomerCases({ page: 1, page_size: 5 }));
  };

  // Get initials for avatar
  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {sanitizeText(user?.firstName) || 'Customer'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your uploads and support cases from your dashboard.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Profile Summary Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem',
                    mr: 2,
                  }}
                >
                  {getInitials()}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {sanitizeText(user?.firstName)} {sanitizeText(user?.lastName)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sanitizeText(user?.email)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Account Status:
                </Typography>
                <Chip
                  label={sanitizeText(user?.status) || 'Active'}
                  color={user?.status === 'active' ? 'success' : 'warning'}
                  size="small"
                />
              </Box>

              {user?.emailVerified !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Email Verified:
                  </Typography>
                  <Chip
                    label={user.emailVerified ? 'Yes' : 'No'}
                    color={user.emailVerified ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={<Settings />}
                component={RouterLink}
                to="/customer/settings"
              >
                Account Settings
              </Button>
            </CardActions>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CloudUpload />}
                  component={RouterLink}
                  to="/"
                  fullWidth
                >
                  Upload Files
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Recent Uploads Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InsertDriveFile sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Recent Uploads</Typography>
                  {uploads.total > 0 && (
                    <Chip
                      label={uploads.total}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={uploads.loading || cases.loading}
                >
                  Refresh
                </Button>
              </Box>

              {uploads.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {sanitizeError(uploads.error)}
                </Alert>
              )}

              {uploads.loading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : uploads.data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography color="text.secondary" gutterBottom>
                    No uploads yet
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    component={RouterLink}
                    to="/"
                    startIcon={<CloudUpload />}
                  >
                    Upload Your First File
                  </Button>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>File Name</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploads.data.map((file) => (
                        <TableRow key={file.id} hover>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {sanitizeText(file.original_name)}
                            </Typography>
                          </TableCell>
                          <TableCell>{sanitizeFileSize(file.file_size)}</TableCell>
                          <TableCell>
                            <StatusChip status={file.status} />
                          </TableCell>
                          <TableCell>{formatDate(file.uploaded_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
            {uploads.data.length > 0 && uploads.total > 5 && (
              <CardActions>
                <Button size="small" component={RouterLink} to="/customer/uploads">
                  View All ({uploads.total})
                </Button>
              </CardActions>
            )}
          </Card>

          {/* My Cases Card */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Folder sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">My Cases</Typography>
                  {cases.total > 0 && (
                    <Chip
                      label={cases.total}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              </Box>

              {cases.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {sanitizeError(cases.error)}
                </Alert>
              )}

              {cases.loading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : cases.data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Folder sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography color="text.secondary">
                    No cases found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cases are created when you upload files
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {cases.data.map((caseItem) => (
                    <Paper
                      key={caseItem.case_number}
                      variant="outlined"
                      sx={{ p: 2 }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {sanitizeText(caseItem.case_number)}
                          </Typography>
                          {caseItem.title && (
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                              {sanitizeText(caseItem.title)}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <PriorityChip priority={caseItem.priority} />
                          <Chip
                            label={sanitizeText(caseItem.status) || 'Active'}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          <InsertDriveFile sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                          {caseItem.file_count} file{caseItem.file_count !== 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Created: {formatDate(caseItem.created_at)}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
            {cases.data.length > 0 && cases.total > 5 && (
              <CardActions>
                <Button size="small" component={RouterLink} to="/customer/cases">
                  View All ({cases.total})
                </Button>
              </CardActions>
            )}
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CustomerDashboard;
